import { useEffect, useState, useRef, useCallback } from "react";
import { Card, Row, Col, Typography, Avatar, Tag, Spin, Divider, Empty, Button, Input, message } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, SafetyOutlined, CreditCardOutlined, PlusOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useFetchTravellerProfileInfo } from "../services/traveller.service";
import { storage } from "../utils/storage";
import { post, patch } from "../helpers/config";

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";

function loadStripeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.Stripe) { resolve(); return; }
        const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
        if (existing) { existing.addEventListener("load", () => resolve(), { once: true }); return; }
        const s = document.createElement("script");
        s.src = "https://js.stripe.com/v3/";
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load Stripe.js"));
        document.head.appendChild(s);
    });
}

const { Title, Text } = Typography;

const TravellerProfile = () => {
    const userId = storage.getUserId() ?? "";
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // --- Card management state ---
    const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
    const [savedCardLast4, setSavedCardLast4] = useState<string | null>(null);
    const [showCardForm, setShowCardForm] = useState(false);
    const [cardForm, setCardForm] = useState({ name: "" });
    const [cardSaving, setCardSaving] = useState(false);
    const stripeLoaded = useRef(false);
    const stripeClientRef = useRef<ReturnType<NonNullable<typeof window.Stripe>> | null>(null);
    const cardElementRef = useRef<StripeCardElement | null>(null);
    const cardMountRef = useRef<HTMLDivElement | null>(null);

    const handleCardChange = useCallback((field: string, value: string) => {
        setCardForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const ensureStripeClient = useCallback(async () => {
        if (!STRIPE_PUBLISHABLE_KEY) {
            throw new Error("Stripe publishable key is not configured.");
        }
        await loadStripeScript();
        if (!window.Stripe) {
            throw new Error("Stripe.js failed to initialize.");
        }
        if (!stripeClientRef.current) {
            stripeClientRef.current = window.Stripe(STRIPE_PUBLISHABLE_KEY);
        }
        return stripeClientRef.current;
    }, []);

    const handleSaveCard = async () => {
        if (!userId) return;
        if (!cardForm.name.trim()) {
            message.error("Please enter the cardholder name.");
            return;
        }
        if (!cardElementRef.current) {
            message.error("Card details are not ready yet. Please wait a second and try again.");
            return;
        }
        setCardSaving(true);
        try {
            if (!stripeLoaded.current) {
                await loadStripeScript();
                stripeLoaded.current = true;
            }
            const stripe = await ensureStripeClient();
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElementRef.current,
                billing_details: {
                    name: cardForm.name.trim(),
                },
            });

            if (error) {
                throw new Error(error.message || "Unable to validate your card details.");
            }
            if (!paymentMethod?.id) {
                throw new Error("Unable to create a Stripe payment method.");
            }

            await patch(`traveller/stripe/card/update?travellerId=${userId}&tokenId=${paymentMethod.id}`, {});
            setSavedCardLast4(paymentMethod.card?.last4 ?? null);
            setShowCardForm(false);
            setCardForm({ name: "" });
            message.success("Card saved successfully!");
        } catch (err: any) {
            message.error(err?.message ?? "Failed to save card. Please try again.");
        } finally {
            setCardSaving(false);
        }
    };

    const fetchProfile = useFetchTravellerProfileInfo({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) setProfile(res.data);
            setLoading(false);
        },
        onError: () => setLoading(false),
    });

    useEffect(() => {
        if (!userId) return;
        fetchProfile.mutate(userId);
        // Fetch Stripe customer to check for saved card
        post(`trip-payment/stripe/customer/create?travellerId=${userId}`, {})
            .then((res: any) => {
                if (res?.data?.customerId) {
                    setStripeCustomerId(res.data.customerId);
                    if (res.data.last4) setSavedCardLast4(res.data.last4);
                }
            })
            .catch(() => { /* no saved card */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    useEffect(() => {
        let isMounted = true;

        const mountCardElement = async () => {
            if (!showCardForm || !cardMountRef.current || cardElementRef.current) {
                return;
            }

            const stripe = await ensureStripeClient();
            if (!isMounted || !cardMountRef.current) {
                return;
            }

            const elements = stripe.elements();
            const cardElement = elements.create("card", { hidePostalCode: true });
            cardElement.mount(cardMountRef.current);
            cardElementRef.current = cardElement;
        };

        mountCardElement().catch((err) => {
            message.error(err?.message ?? "Failed to initialize card input.");
        });

        return () => {
            isMounted = false;
            if (cardElementRef.current) {
                cardElementRef.current.destroy();
                cardElementRef.current = null;
            }
        };
    }, [showCardForm, ensureStripeClient]);

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!profile) {
        return <Empty description="Profile not found" style={{ marginTop: 80 }} />;
    }

    const statusColor =
        profile.travellerStatusName === "Active" ? "green" :
        profile.travellerStatusName === "Inactive" ? "orange" : "red";

    const infoRows = [
        { icon: <MailOutlined />, label: "Email", value: profile.email ?? "—" },
        { icon: <PhoneOutlined />, label: "Phone", value: profile.phoneNo ? `+${profile.phoneNoDialingCode ?? ""} ${profile.phoneNo}` : "—" },
        { icon: <EnvironmentOutlined />, label: "Address", value: profile.address ?? "—" },
        { icon: <UserOutlined />, label: "Gender", value: profile.gender ?? "—" },
    ];

    const verificationRows = [
        { label: "Email Verified", value: profile.isEmailVerified },
        { label: "Phone Verified", value: profile.isPhoneNoVerified },
    ];

    const emergencyRows = [
        { label: "Contact Name", value: profile.emergencyName ?? "—" },
        {
            label: "Phone",
            value: profile.emergencyMobileNumber
                ? `+${profile.emergencyMobileNumberDialingCode ?? ""} ${profile.emergencyMobileNumber}`
                : "—",
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <Text style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", display: "block" }}>My Profile</Text>
                <Text style={{ color: "#64748b", fontSize: 14 }}>Your personal information and account details.</Text>
            </div>

            <Row gutter={[20, 20]}>
                {/* Left: Avatar + name */}
                <Col xs={24} lg={8}>
                    <Card
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", textAlign: "center" }}
                        bodyStyle={{ padding: "32px 24px" }}
                    >
                        <Avatar
                            size={96}
                            src={profile.imageUrl}
                            style={{ background: "linear-gradient(135deg, #1a1a2e, #6366f1)", fontSize: 36, marginBottom: 16 }}
                        >
                            {(profile.fullName ?? profile.email ?? "T").charAt(0).toUpperCase()}
                        </Avatar>
                        <Title level={4} style={{ margin: 0, color: "#1e293b" }}>
                            {profile.fullName ?? "—"}
                        </Title>
                        <Text style={{ color: "#64748b", fontSize: 13 }}>{profile.email}</Text>
                        <div style={{ marginTop: 14 }}>
                            <Tag color={statusColor} style={{ fontSize: 12, padding: "2px 12px", borderRadius: 20 }}>
                                {profile.travellerStatusName ?? "Active"}
                            </Tag>
                        </div>

                        <Divider style={{ margin: "20px 0" }} />

                        {verificationRows.map((row) => (
                            <div
                                key={row.label}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "6px 0",
                                }}
                            >
                                <Text style={{ color: "#64748b", fontSize: 13 }}>{row.label}</Text>
                                <Tag color={row.value ? "green" : "red"}>
                                    {row.value ? "Verified" : "Not Verified"}
                                </Tag>
                            </div>
                        ))}
                    </Card>
                </Col>

                {/* Right: Details */}
                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                <UserOutlined style={{ marginRight: 8, color: "#6366f1" }} />
                                Personal Information
                            </span>
                        }
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", marginBottom: 20 }}
                        bodyStyle={{ padding: "20px 24px" }}
                    >
                        <Row gutter={[16, 16]}>
                            {infoRows.map((row) => (
                                <Col xs={24} sm={12} key={row.label}>
                                    <div style={{ padding: "12px 16px", background: "#f8fafc", borderRadius: 10 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                            <span style={{ color: "#6366f1" }}>{row.icon}</span>
                                            <Text style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                {row.label}
                                            </Text>
                                        </div>
                                        <Text style={{ color: "#1e293b", fontSize: 14, fontWeight: 500 }}>{row.value}</Text>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    <Card
                        title={
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                <SafetyOutlined style={{ marginRight: 8, color: "#f59e0b" }} />
                                Emergency Contact
                            </span>
                        }
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", marginBottom: 20 }}
                        bodyStyle={{ padding: "20px 24px" }}
                    >
                        <Row gutter={[16, 16]}>
                            {emergencyRows.map((row) => (
                                <Col xs={24} sm={12} key={row.label}>
                                    <div style={{ padding: "12px 16px", background: "#fffbeb", borderRadius: 10 }}>
                                        <Text style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                                            {row.label}
                                        </Text>
                                        <Text style={{ color: "#1e293b", fontSize: 14, fontWeight: 500 }}>{row.value}</Text>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    {/* Payment & Cards */}
                    <Card
                        title={
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                <CreditCardOutlined style={{ marginRight: 8, color: "#6366f1" }} />
                                Payment & Cards
                            </span>
                        }
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                        bodyStyle={{ padding: "20px 24px" }}
                    >
                        {savedCardLast4 ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", background: "#f0fdf4", borderRadius: 12, marginBottom: 16, border: "1px solid #bbf7d0" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <CheckCircleFilled style={{ color: "#22c55e", fontSize: 20 }} />
                                    <div>
                                        <Text style={{ fontWeight: 600, color: "#1e293b", display: "block" }}>Card on file</Text>
                                        <Text style={{ color: "#64748b", fontSize: 13 }}>•••• •••• •••• {savedCardLast4}</Text>
                                    </div>
                                </div>
                                <Button
                                    size="small"
                                    onClick={() => setShowCardForm((v) => !v)}
                                    style={{ borderRadius: 8, borderColor: "#6366f1", color: "#6366f1" }}
                                >
                                    {showCardForm ? "Cancel" : "Replace"}
                                </Button>
                            </div>
                        ) : (
                            <div style={{ marginBottom: 16 }}>
                                <Text style={{ color: "#64748b", fontSize: 14 }}>No card saved yet.</Text>
                            </div>
                        )}

                        {(!savedCardLast4 || showCardForm) && (
                            <div style={{ background: "#f8fafc", borderRadius: 12, padding: "18px 20px", border: "1px solid #e2e8f0" }}>
                                {!savedCardLast4 && (
                                    <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={() => setShowCardForm((v) => !v)}
                                        style={{ marginBottom: 16, borderRadius: 8, color: "#6366f1", borderColor: "#6366f1" }}
                                    >
                                        Add a Card
                                    </Button>
                                )}
                                {showCardForm && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                        <div>
                                            <Text style={{ color: "#64748b", fontSize: 12, display: "block", marginBottom: 4 }}>CARDHOLDER NAME</Text>
                                            <Input
                                                placeholder="Name on card"
                                                value={cardForm.name}
                                                onChange={(e) => handleCardChange("name", e.target.value)}
                                                style={{ borderRadius: 8 }}
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ color: "#64748b", fontSize: 12, display: "block", marginBottom: 4 }}>CARD DETAILS</Text>
                                            <div
                                                ref={cardMountRef}
                                                style={{
                                                    borderRadius: 8,
                                                    border: "1px solid #d9d9d9",
                                                    background: "#fff",
                                                    minHeight: 42,
                                                    padding: "10px 12px",
                                                }}
                                            />
                                        </div>
                                        <Button
                                            type="primary"
                                            loading={cardSaving}
                                            onClick={handleSaveCard}
                                            style={{ borderRadius: 8, background: "#6366f1", borderColor: "#6366f1", marginTop: 4 }}
                                        >
                                            Save Card
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default TravellerProfile;
